import React, { useEffect, useState } from "react";
import AppLogo from "../../assets/images/AppLogo.png";
import AppStore from "../../assets/images/AppStore.png";
import GooglePlay from "../../assets/images/GooglePlay.png";
import "./style.css";
import { FormattedMessage, useIntl } from "react-intl";
import axios from "axios";
import { base_url, play_store_url, app_store_url } from "../../constants";
import { getProfile } from "../../api/GetProfile";
import { getSubscriptionPlans } from "../../api/Subscribers/subscriptionPlan";
import PlanCard, {
  hasSimpleInterval,
  toggleMessageId,
} from "../../components/Subscription/PlanCard";
import { useDispatch, useSelector } from "react-redux";
import { SimpleCatchError } from "../../utils/Helper";
import backIcon from "../../assets/images/backButton.svg";
import { useNavigate } from "react-router";
import Loader from "../../components/GeneralComponents";
import { isStripe } from "../../utils/paymentProvider";
import {
  createSubscriptionCheckout,
  createSubscriptionPortal,
} from "../../api/stripeApi/subscription";
import { redirectToStripe } from "../../utils/stripeRedirect";
import { handleReload } from "../../components/Header/Helper";
export const Subscription = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const token = searchParams.get("token");
  const id = searchParams.get("typed_identifier");
  const user_id = searchParams.get("identifier");
  const user_type = searchParams.get("identifier_type");
  const userData = JSON.parse(localStorage.getItem("data"));
  const [plans, setPlans] = useState(null);
  const [selectedPlanId, setSelectedPlanId] = useState(
    Number(searchParams.get("plan")) || null
  );
  const [loading, setLoading] = useState(false);
  const access_token = token ? token : userData?.accessToken;
  const user_type_id = id ? id : userData?.id;
  const main_user_id = user_id ? user_id : userData?.user_id;
  const userType = user_type ? user_type : userData?.user_type;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const intl = useIntl();
  const userTypeData = useSelector((state) => state?.common?.profile);
  // Inside the mobile CustomWebView the page is opened with a `?token=` deep
  // link; on desktop web there is no token. Used to hide the web chrome and to
  // guard the identity-dependent fetches. The token param is dropped when
  // Stripe's Checkout/Portal returns to this page (SEC-4 strips it, and the
  // Portal "back" link carries no params), so the WebView marker is persisted
  // in sessionStorage — scoped to this tab/WebView session, no sensitive value
  // in the URL — and read back on the return load.
  const isWebView =
    Boolean(token) || sessionStorage.getItem("isWebView") === "true";
  const hasIdentity = Boolean(main_user_id && user_type_id && userType);
  // While the profile is still loading (we have identity to fetch it but the
  // Redux profile hasn't populated yet), `subscribed` is undefined and the
  // button would wrongly render "Subscribe" and route to Checkout for an
  // already-subscribed user. Hold the button until the profile resolves.
  const profilePending = hasIdentity && !userTypeData;
  // `null` means still loading. An empty array means either every plan is
  // disabled or the catalog could not be fetched — the API layer returns [] on
  // error, so the two are deliberately not distinguished here; both mean "nothing
  // to sell right now" and both render the same message.
  const plansLoading = plans === null;
  const planUnavailable = plansLoading || plans.length === 0;
  // /subscription is a public route, so this page renders for signed-out
  // visitors. Checkout is not public — POSTing to it without a JWT returns 401,
  // and the shared axios interceptor turns any 401 into a session clear and a
  // redirect. Until this fetch was made unconditional, the plan simply never
  // loaded when signed out and the button stayed disabled, which is what kept
  // anonymous visitors out of that path. Send them to sign-up instead.
  const canCheckout = hasIdentity || Boolean(access_token);
  const selectedPlan =
    (plans || []).find((plan) => plan.id === selectedPlanId) ||
    (plans || []).find((plan) => plan.highlighted) ||
    (plans || [])[0];
  const renderedPlans = isStripe() ? plans || [] : (plans || []).slice(0, 1);
  // A phone cannot show two full cards without pushing the second below the
  // fold, so below 768px the stylesheet renders only the selected one and this
  // control switches between them. It is pointless with a single card, and
  // leaving it off also keeps the grid from hiding that card: the hide rule is
  // scoped to the modifier this flag adds.
  const showPlanToggle = renderedPlans.length > 1;
  useEffect(() => {
    if (token) {
      sessionStorage.setItem("isWebView", "true");
    }
    loadPlans();
    if (hasIdentity) {
      getProfile(dispatch, main_user_id, user_type_id, userType, token);
    }
  }, []);
  const loadPlans = async () => {
    setLoading(true);
    try {
      const list = await getSubscriptionPlans();
      setPlans(list);
    } finally {
      setLoading(false);
    }
  };
  // Stripe Billing: subscribe -> hosted Checkout (mode:subscription);
  // manage/unsubscribe -> Customer Portal. Both return a hosted url we redirect
  // to. The token is stripped from the URL first (SEC-4). Provisioning is
  // webhook-driven server-side, so nothing is marked subscribed here.
  const handleStripeSubscribe = async (planId) => {
    setLoading(true);
    const origin = window.location.origin;
    // Route by whether the user HAS an active subscription (`subscribed`), not
    // by whether it will renew (`nextSubscription`). A user who cancelled during
    // their trial is still subscribed (access retained until period end) with
    // renewal off — they must reach the Customer Portal to manage/resume, not
    // the Checkout path (which the backend rejects with "already subscribed").
    const result = userTypeData?.subscribed
      ? await createSubscriptionPortal(
          {
            returnUrl: `${origin}/subscription`,
          },
          token
        )
      : await createSubscriptionCheckout(
          {
            successUrl: `${origin}/subscription?result=true`,
            cancelUrl: `${origin}/subscription`,
            planId,
          },
          token
        );
    if (redirectToStripe(result?.url)) {
      return;
    }
    // The plan can be disabled between this page rendering and the click. The
    // server rejects it rather than charging for a retired plan, so refresh the
    // list instead of leaving a card on screen that can no longer be bought.
    await loadPlans();
    setLoading(false);
  };

  const handleAeropaySubscribe = async () => {
    if (userTypeData?.nextSubscription) {
      const body = { userId: main_user_id };
      setLoading(true);
      await axios.post(`${base_url}payment/unsubscribe`, body, {
        headers: {
          Authorization: `Bearer ${token ? token : access_token}`,
          "ngrok-skip-browser-warning": true,
        },
      });
      setLoading(false);
      navigate("/");
    } else {
      const body = {
        userId: main_user_id,
        homeownerId: userType === "homeowner" ? user_type_id : null,
        tradesmanId: userType === "tradesman" ? user_type_id : null,
        contractorId: userType === "contractor" ? user_type_id : null,
      };
      setLoading(true);
      await axios.post(`${base_url}payment/subscribe`, body, {
        headers: {
          Authorization: `Bearer ${token ? token : access_token}`,
          "ngrok-skip-browser-warning": true,
        },
      });
      setLoading(false);
      navigate(token ? "/subscription?result=true" : "/");
    }
  };

  const handleSubscribe = async (planId) => {
    try {
      if (isStripe()) {
        await handleStripeSubscribe(planId);
      } else {
        await handleAeropaySubscribe();
      }
    } catch (error) {
      setLoading(false);
      SimpleCatchError(error);
    }
  };
  return (
    <>
      {loading && (
        <div className="search-loader">
          <Loader />
        </div>
      )}
      <div className="container">
        {!isWebView && (
          <>
            <div className="subscription-header d-flex justify-content-between align-items-center">
              <div className=" d-flex justify-content-start align-items-center">
                <img
                  src={AppLogo}
                  alt="logo"
                  height={40}
                  style={{ cursor: "pointer" }}
                  onClick={() => handleReload(navigate)}
                />
              </div>
              <div className=" d-flex justify-content-center align-items-center">
                <div className="d-flex justify-content-center ms-3 store-icons">
                  <a href={play_store_url} target="_blank" rel="noreferrer">
                    <img
                      src={GooglePlay}
                      alt="Google Play"
                      style={{ width: "100px" }}
                    />
                  </a>
                  <a href={app_store_url} target="_blank" rel="noreferrer">
                    <img
                      src={AppStore}
                      alt="App Store"
                      style={{ width: "100px" }}
                    />
                  </a>
                </div>
              </div>
            </div>
            <div>
              <span className="d-flex align-items-center">
                <img
                  src={backIcon}
                  alt=""
                  onClick={() => navigate(-1)}
                  style={{ cursor: "pointer" }}
                  className="shadow-lg border rounded"
                />
                <span className="text-black fs-4 m-1">
                  <FormattedMessage id="back" />
                </span>
              </span>
            </div>
          </>
        )}

        <div className="w-100 d-flex justify-content-center align-items-center mt-5">
          <span className="subscription-heading">
            <FormattedMessage id="subscription" />
          </span>
          <span className="subscription-heading2">
            <FormattedMessage id="plan" />
          </span>
        </div>
        {plans && plans.length === 0 ? (
          <div className="subscription-empty" data-testid="plans-unavailable">
            <FormattedMessage id="plans_unavailable" />
          </div>
        ) : (
          <>
            {showPlanToggle && (
              <div
                className="subscription-plans-toggle"
                role="radiogroup"
                aria-label={intl.formatMessage({ id: "choose_plan" })}
              >
                {renderedPlans.map((plan) => (
                  <button
                    key={plan.id}
                    type="button"
                    role="radio"
                    aria-checked={selectedPlan?.id === plan.id}
                    className={[
                      "subscription-plans-toggle__option",
                      selectedPlan?.id === plan.id
                        ? "subscription-plans-toggle__option--active"
                        : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    onClick={() => setSelectedPlanId(plan.id)}
                    data-testid={`plan-toggle-${plan.id}`}
                  >
                    {hasSimpleInterval(plan) ? (
                      <FormattedMessage id={toggleMessageId(plan)} />
                    ) : (
                      plan.name || `${plan.intervalCount} ${plan.interval}`
                    )}
                  </button>
                ))}
              </div>
            )}
            <div
              className={[
                "subscription-plans-grid",
                showPlanToggle ? "subscription-plans-grid--switchable" : "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              {renderedPlans.map((plan) => (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  selected={
                    (plans || []).length > 1 && selectedPlan?.id === plan.id
                  }
                  onSelect={
                    (plans || []).length > 1
                      ? () => setSelectedPlanId(plan.id)
                      : undefined
                  }
                  onCta={() => {
                    setSelectedPlanId(plan.id);
                    if (!canCheckout) {
                      navigate(`/welcome?plan=${plan.id}`);
                      return;
                    }
                    handleSubscribe(plan.id);
                  }}
                  showTrial={!userTypeData?.subscribed}
                  ctaLoading={loading || profilePending}
                  ctaDisabled={profilePending || planUnavailable}
                  ctaLabel={
                    !canCheckout ? (
                      <FormattedMessage id="register" />
                    ) : !userTypeData?.subscribed ? (
                      <FormattedMessage id="subscribe" />
                    ) : (
                      <FormattedMessage id="unsubscribe" />
                    )
                  }
                />
              ))}
            </div>
          </>
        )}
      </div>
    </>
  );
};
